import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  Stack,
  Typography,
  lighten,
} from "@mui/material";
// import countries from "i18n-iso-countries";
// import enLocale from "i18n-iso-countries/langs/en.json";
import { zodResolver } from "@hookform/resolvers/zod";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { SettingsWrapper } from "../../../Billing/partials/SettingsWrapper";
import { FormLabel, Input, TAvatarUploadError } from "../../../../components";
import { changePersonalInfoValidationSchema } from "../../../../constants/accountSettings";
import {
  BG_COLORS,
  BORDER_COLORS,
  FAILED_TO_CHANGE_PROFILE_INFO,
  FAILED_TO_UPLOAD_AVATAR,
  FONT_COLORS,
  MAX_FILE_SIZE_EXCEEDED,
  STATUS_COLORS,
} from "../../../../constants";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { updateUser } from "../../../../features/user/user.thunk";
import AvatarUpload from "../../../../components/AvatarUpload";

type TFormFields = {
  fullName: string;
  email: string;
  // country: string;
  avatar: string;
};

// Register country names
// countries.registerLocale(enLocale);

const MAX_AVATAR_SIZE = 10 * 1024 * 1024; // 10MB
const AVATAR_SUPPORTED_TYPES = "image/png, image/jpeg";

// Prepare country options
// const countriesMap = countries.getNames("en", { select: "official" });

export const PersonalInfo = () => {
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const user = useAppSelector((state) => state.user.user);
  const pendingEmail = useAppSelector((state) => state.user.user?.pendingEmail);

  const dispatch = useAppDispatch();

  const defaultValues = useMemo(
    () => ({
      email: user?.email || "",
      avatar: user?.photoName || "",
      fullName: user?.fullName || "",
    }),
    [user],
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<TFormFields>({
    defaultValues,
    resolver: zodResolver(changePersonalInfoValidationSchema),
    reValidateMode: "onSubmit",
  });

  const avatar = watch("avatar");

  const onSubmit = handleSubmit(async (data) => {
    const formData = new FormData();

    formData.append("fullName", data.fullName);
    formData.append("email", data.email);
    // formData.append("country", data.country);

    if (!avatar) {
      // The avatar was deleted, so delete it also in DB
      formData.append("isPhotoDeleted", "true");
    } else if (avatar && avatarFile) {
      // The avatar was changed, so append the new file
      formData.append("photo", avatarFile);
    }

    try {
      await dispatch(updateUser(formData)).unwrap();
    } catch {
      enqueueSnackbar(FAILED_TO_CHANGE_PROFILE_INFO, { variant: "error" });
    }
  });

  const handleAvatarChange = (src: string, file: File) => {
    setValue("avatar", src, { shouldDirty: true });
    setAvatarFile(file);
  };

  const handleRemoveAvatar = () => {
    setValue("avatar", "", { shouldDirty: true });
    setAvatarFile(null);
  };

  const handleAvatarUploadError = (error: TAvatarUploadError) => {
    if (error === "file-too-large") {
      enqueueSnackbar(FAILED_TO_UPLOAD_AVATAR, { variant: "error" });
    } else {
      enqueueSnackbar(MAX_FILE_SIZE_EXCEEDED, { variant: "error" });
    }
  };

  useEffect(() => {
    if (user) {
      reset(defaultValues);
    }
  }, [user, reset, defaultValues]);

  return (
    <SettingsWrapper
      heading="Personal Information"
      subHeading="Changing your email will require you to verify your new email address"
    >
      <Stack
        component="form"
        alignItems="flex-start"
        gap={1.5}
        onSubmit={onSubmit}
        noValidate
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          width="100%"
          px={3}
          py={2}
          border={
            avatar
              ? `1px solid ${BORDER_COLORS.info}`
              : `1px solid ${lighten(BORDER_COLORS.default, 0.5)}`
          }
          borderRadius={1}
          bgcolor={
            avatar ? lighten(BG_COLORS.primary, 0.95) : BG_COLORS.default
          }
        >
          <AvatarUpload
            sx={{ width: 64, height: 64 }}
            src={avatar}
            alt="User Avatar"
            disabled={isSubmitting}
            accept={AVATAR_SUPPORTED_TYPES}
            maxFileSize={MAX_AVATAR_SIZE}
            onFileChange={handleAvatarChange}
            onUploadError={handleAvatarUploadError}
          />
          <Box flex={1}>
            <Typography
              fontSize={13}
              fontWeight={500}
              color={lighten(FONT_COLORS.default, 0.35)}
            >
              Max file size:
            </Typography>
            <Typography fontWeight={500} color={FONT_COLORS.info}>
              10MB
            </Typography>
            <Typography
              mt={0.5}
              fontSize={13}
              fontWeight={500}
              color={lighten(FONT_COLORS.default, 0.35)}
            >
              Supported types:
            </Typography>
            <Typography fontWeight={500} color={FONT_COLORS.info}>
              .jpg, .jpeg, .png
            </Typography>
          </Box>
          {!!avatar && (
            <Button
              disabled={isSubmitting}
              color="error"
              onClick={handleRemoveAvatar}
            >
              Remove
            </Button>
          )}
        </Stack>

        <Controller
          control={control}
          name="fullName"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              disabled={isSubmitting}
              error={fieldState.invalid}
            >
              <FormLabel htmlFor="fullName" sx={{ fontSize: 13 }}>
                Full Name
              </FormLabel>
              <Input
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={isSubmitting}
                id="fullName"
                placeholder="Full name"
                size="small"
              />
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              disabled={isSubmitting}
              error={fieldState.invalid}
            >
              <FormLabel htmlFor="email" sx={{ fontSize: 13 }}>
                Email Address
              </FormLabel>
              <Input
                {...field}
                type="email"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={isSubmitting}
                id="email"
                placeholder="Email address"
                size="small"
              />
              {pendingEmail && !isSubmitting && (
                <FormLabel
                  error={false}
                  disabled={false}
                  sx={{
                    fontSize: 11,
                    color: STATUS_COLORS.success,
                    cursor: "text",
                  }}
                >
                  Confirmation email has been sent to <b>{pendingEmail}</b>
                </FormLabel>
              )}
            </FormControl>
          )}
        />

        {/*<Stack*/}
        {/*  flexDirection="row"*/}
        {/*  justifyContent="space-between"*/}
        {/*  gap={2}*/}
        {/*  width="100%"*/}
        {/*  sx={{*/}
        {/*    [theme.breakpoints.down("sm")]: {*/}
        {/*      flexDirection: "column",*/}
        {/*    },*/}
        {/*  }}*/}
        {/*>*/}
        {/*  <Controller*/}
        {/*    control={control}*/}
        {/*    name="email"*/}
        {/*    render={({ field, fieldState }) => (*/}
        {/*      <FormControl*/}
        {/*        fullWidth*/}
        {/*        disabled={isSubmitting}*/}
        {/*        error={fieldState.invalid}*/}
        {/*      >*/}
        {/*        <FormLabel htmlFor="email" sx={{ fontSize: 13 }}>*/}
        {/*          Email Address*/}
        {/*        </FormLabel>*/}
        {/*        <Input*/}
        {/*          {...field}*/}
        {/*          type="email"*/}
        {/*          error={fieldState.invalid}*/}
        {/*          helperText={fieldState.error?.message}*/}
        {/*          disabled={isSubmitting}*/}
        {/*          id="email"*/}
        {/*          placeholder="Email address"*/}
        {/*          size="small"*/}
        {/*        />*/}
        {/*      </FormControl>*/}
        {/*    )}*/}
        {/*  />*/}
        {/*  <Controller*/}
        {/*    control={control}*/}
        {/*    name="country"*/}
        {/*    render={({ field }) => (*/}
        {/*      <FormControl fullWidth disabled={isSubmitting}>*/}
        {/*        <FormLabel sx={{ fontSize: 13 }} focused={false}>*/}
        {/*          Country*/}
        {/*        </FormLabel>*/}
        {/*        <Select*/}
        {/*          {...field}*/}
        {/*          disabled={isSubmitting}*/}
        {/*          slotProps={{*/}
        {/*            input: {*/}
        {/*              sx: {*/}
        {/*                fontSize: 13,*/}
        {/*                fontWeight: 500,*/}
        {/*                padding: "3px 12px 3px",*/}
        {/*                color: FONT_COLORS.default,*/}
        {/*              },*/}
        {/*            },*/}
        {/*          }}*/}
        {/*        >*/}
        {/*          {Object.entries(countriesMap).map(([code, country]) => (*/}
        {/*            <MenuItem key={code} value={code}>*/}
        {/*              {country}*/}
        {/*            </MenuItem>*/}
        {/*          ))}*/}
        {/*        </Select>*/}
        {/*      </FormControl>*/}
        {/*    )}*/}
        {/*  />*/}
        {/*</Stack>*/}

        <Stack
          flexDirection="row"
          justifyContent="flex-end"
          gap={1}
          width="100%"
        >
          <Button
            disabled={isSubmitting || !isDirty}
            type="reset"
            onClick={() => reset(defaultValues)}
            sx={{ px: 2, py: 2 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={!isDirty}
            loading={isSubmitting}
            sx={{ px: 2, py: 2 }}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </SettingsWrapper>
  );
};
